/**
 * useConnectorLibraryState - URL-driven state for the Connector Library page.
 *
 * Owns search query, platform filter (multi-select), status filter (multi-select),
 * sort column/direction, and the 1-based table page (#2161). All state lives in
 * `useSearchParams`. Drawer params (handled by useConnectorPageState) are
 * preserved untouched on every mutation.
 *
 * URL schema:
 *   /connectors?q=linked&platform=google_ads,linkedin_ads&status=error&sort=name:asc&page=3
 *
 * `page` is in the URL so the detail page's Connectors back link (which
 * replays the last library URL) and browser Back return to the same page.
 * Changing the search, a filter or the sort resets it to page 1.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ConnectorStatus } from '@/types/connectors';

export type SortColumn = 'name' | 'status' | 'last_run' | 'next_run';
export type SortDirection = 'asc' | 'desc';

export interface ConnectorLibrarySort {
  column: SortColumn;
  direction: SortDirection;
}

const DEFAULT_SORT: ConnectorLibrarySort = { column: 'name', direction: 'asc' };

const VALID_SORT_COLUMNS: ReadonlySet<SortColumn> = new Set([
  'name',
  'status',
  'last_run',
  'next_run',
]);

function parseList(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function parseSort(raw: string | null): ConnectorLibrarySort {
  if (!raw) return DEFAULT_SORT;
  const [colRaw, dirRaw] = raw.split(':');
  const column = VALID_SORT_COLUMNS.has(colRaw as SortColumn)
    ? (colRaw as SortColumn)
    : DEFAULT_SORT.column;
  const direction: SortDirection = dirRaw === 'desc' ? 'desc' : 'asc';
  return { column, direction };
}

function parsePage(raw: string | null): number {
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : 1;
}

function serializeSort(sort: ConnectorLibrarySort): string {
  return `${sort.column}:${sort.direction}`;
}

function isDefaultSort(sort: ConnectorLibrarySort): boolean {
  return sort.column === DEFAULT_SORT.column && sort.direction === DEFAULT_SORT.direction;
}

export interface UseConnectorLibraryStateReturn {
  q: string;
  platforms: string[];
  statuses: ConnectorStatus[];
  sort: ConnectorLibrarySort;
  /** 1-based page of the connector table. */
  page: number;
  setQ: (q: string) => void;
  setPlatforms: (platforms: string[]) => void;
  togglePlatform: (platform: string) => void;
  setStatuses: (statuses: ConnectorStatus[]) => void;
  toggleStatus: (status: ConnectorStatus) => void;
  cycleSort: (column: SortColumn) => void;
  /** Set the sort directly (a table header click) and reset to page 1. */
  setSort: (sort: ConnectorLibrarySort | null) => void;
  setPage: (page: number) => void;
  clearAll: () => void;
}

export function useConnectorLibraryState(): UseConnectorLibraryStateReturn {
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const platforms = useMemo(() => parseList(params.get('platform')), [params]);
  const statuses = useMemo(
    () => parseList(params.get('status')) as ConnectorStatus[],
    [params],
  );
  const sort = useMemo(() => parseSort(params.get('sort')), [params]);
  const page = parsePage(params.get('page'));

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // A new search, filter or sort changes which rows exist, so start at page 1.
  // Only when something actually changed: re-setting the same value (e.g. the
  // debounced search input settling on mount) must not lose the page.
  const updateFromPageOne = useCallback(
    (mutate: (next: URLSearchParams) => void) =>
      update((next) => {
        const before = next.toString();
        mutate(next);
        if (next.toString() !== before) next.delete('page');
      }),
    [update],
  );

  const setQ = useCallback(
    (newQ: string) =>
      updateFromPageOne((next) => {
        if (newQ) next.set('q', newQ);
        else next.delete('q');
      }),
    [updateFromPageOne],
  );

  const setPlatforms = useCallback(
    (next: string[]) =>
      updateFromPageOne((p) => {
        if (next.length) p.set('platform', next.join(','));
        else p.delete('platform');
      }),
    [updateFromPageOne],
  );

  const togglePlatform = useCallback(
    (platform: string) => {
      const next = platforms.includes(platform)
        ? platforms.filter((p) => p !== platform)
        : [...platforms, platform];
      setPlatforms(next);
    },
    [platforms, setPlatforms],
  );

  const setStatuses = useCallback(
    (next: ConnectorStatus[]) =>
      updateFromPageOne((p) => {
        if (next.length) p.set('status', next.join(','));
        else p.delete('status');
      }),
    [updateFromPageOne],
  );

  const toggleStatus = useCallback(
    (status: ConnectorStatus) => {
      const next = statuses.includes(status)
        ? statuses.filter((s) => s !== status)
        : [...statuses, status];
      setStatuses(next);
    },
    [statuses, setStatuses],
  );

  const cycleSort = useCallback(
    (column: SortColumn) =>
      updateFromPageOne((next) => {
        // Cycle for the same column: asc → desc → reset to default.
        // Different column: jump to that column ascending.
        let nextSort: ConnectorLibrarySort;
        if (sort.column !== column) {
          nextSort = { column, direction: 'asc' };
        } else if (sort.direction === 'asc') {
          nextSort = { column, direction: 'desc' };
        } else {
          nextSort = DEFAULT_SORT;
        }
        if (isDefaultSort(nextSort)) next.delete('sort');
        else next.set('sort', serializeSort(nextSort));
      }),
    [sort, updateFromPageOne],
  );

  const clearAll = useCallback(
    () =>
      updateFromPageOne((next) => {
        next.delete('q');
        next.delete('platform');
        next.delete('status');
        next.delete('sort');
      }),
    [updateFromPageOne],
  );

  const setSort = useCallback(
    (nextSort: ConnectorLibrarySort | null) =>
      updateFromPageOne((next) => {
        if (!nextSort || isDefaultSort(nextSort)) next.delete('sort');
        else next.set('sort', serializeSort(nextSort));
      }),
    [updateFromPageOne],
  );

  const setPage = useCallback(
    (nextPage: number) =>
      update((next) => {
        if (nextPage > 1) next.set('page', String(nextPage));
        else next.delete('page');
      }),
    [update],
  );

  return {
    q,
    platforms,
    statuses,
    sort,
    page,
    setQ,
    setPlatforms,
    togglePlatform,
    setStatuses,
    toggleStatus,
    cycleSort,
    setSort,
    setPage,
    clearAll,
  };
}
